import { AlertPrefsCard } from "./AlertPrefsCard";
import { pickPortfolio } from "./selectedPortfolio";
import React, { useEffect, useState, useCallback } from "react";
import { API } from "./shared";
import { Loader2, UserPlus, Mail, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  portfolio_owner: { label: "Portfolio Owner", cls: "bg-red-50 text-red-700 border-red-200" },
  portfolio_admin: { label: "Portfolio Administrator", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  portfolio_member: { label: "Portfolio Member", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  portfolio_viewer: { label: "Portfolio Viewer", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};
const STATUS_BADGE = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  invited: { label: "Invitation Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function PortfolioTeam() {
  const [pid, setPid] = useState(null);
  const [pname, setPname] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", role: "portfolio_admin" });

  const load = useCallback(async (portfolioId) => {
    const r = await fetch(`${API}/api/portfolio/${portfolioId}/team`, { credentials: "include" });
    if (r.ok) setData(await r.json());
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/portfolio/me`, { credentials: "include" });
        const me = await r.json();
        const p = pickPortfolio(me.portfolios);
        if (!p) { setLoading(false); return; }
        setPid(p.id); setPname(p.name);
        await load(p.id);
      } finally { setLoading(false); }
    })();
  }, [load]);

  const invite = async () => {
    if (!form.email.trim()) { toast.error("Enter an email address"); return; }
    setBusy("invite");
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/team/invite`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Invite failed");
      toast.success(d.already_member ? "Already on the team" : d.email_sent ? "Invitation sent" : "Invitation created");
      setShowInvite(false); setForm({ email: "", first_name: "", last_name: "", role: "portfolio_admin" });
      load(pid);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const resend = async (m) => {
    setBusy(m.id);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/team/${m.id}/resend`, { method: "POST", credentials: "include" });
      const d = await r.json();
      toast.success(d.already_active ? "Member is already active" : "Invitation resent");
    } catch { toast.error("Could not resend"); } finally { setBusy(""); }
  };

  const changeRole = async (m, role) => {
    setBusy(m.id);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/team/${m.id}`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not change role");
      toast.success("Role updated"); load(pid);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    setBusy(m.id);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/team/${m.id}`, { method: "DELETE", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not remove");
      toast.success("Team member removed"); load(pid);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  if (loading) return <div className="py-16 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!pid) return <div className="py-16 text-center text-slate-500" data-testid="team-no-portfolio">No portfolio found for your account.</div>;

  const canManage = data?.can_manage;
  const members = data?.members || [];

  return (
    <div data-testid="portfolio-team-page">
      <div className="mb-5"><AlertPrefsCard pid={pid} /></div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-xl font-bold text-slate-900">Team</h1>
        {canManage && (
          <button onClick={() => setShowInvite(true)} data-testid="team-invite-open"
            className="inline-flex items-center gap-2 rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white text-sm font-medium px-4 py-2">
            <UserPlus className="h-4 w-4" /> Invite Team Member
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-5">People with access to <strong>{pname}</strong>.</p>

      <div className="space-y-3">
        {members.map((m) => {
          const rb = ROLE_BADGE[m.role] || ROLE_BADGE.portfolio_member;
          const sb = STATUS_BADGE[m.status] || STATUS_BADGE.invited;
          return (
            <div key={m.id} data-testid={`team-member-${m.id}`}
              className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{m.name}</div>
                <div className="text-[13px] text-slate-500 truncate flex items-center gap-1"><Mail className="h-3 w-3" />{m.email}</div>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full border ${rb.cls} whitespace-nowrap`}>{rb.label}</span>
              <span className={`text-xs px-2.5 py-1 rounded-full border ${sb.cls} whitespace-nowrap`}>{sb.label}</span>
              {canManage && (
                <div className="flex items-center flex-wrap gap-2 sm:shrink-0">
                  {!m.is_owner && (
                    <select value={m.role} onChange={(e) => changeRole(m, e.target.value)} disabled={busy === m.id}
                      data-testid={`team-role-${m.id}`}
                      className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
                      {(data.assignable_roles || []).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  )}
                  {m.status === "invited" && (
                    <button onClick={() => resend(m)} disabled={busy === m.id} data-testid={`team-resend-${m.id}`}
                      className="text-xs text-blue-700 hover:underline">Resend</button>
                  )}
                  {!m.is_owner && (
                    <button onClick={() => remove(m)} disabled={busy === m.id} data-testid={`team-remove-${m.id}`}
                      className="text-slate-400 hover:text-red-600" title="Remove"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {members.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="team-empty">
            <ShieldCheck className="h-8 w-8 mx-auto text-slate-300" />
            <p className="mt-3 text-sm">Invite your first teammate to help manage this portfolio.</p>
          </div>
        )}
      </div>

      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowInvite(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="team-invite-modal">
            <h2 className="text-lg font-bold text-slate-900">Invite a Team Member</h2>
            <p className="text-[13px] text-slate-500 mt-1">They'll get an email to join {pname}. If they already have an account, they can sign in to accept.</p>
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-slate-500">First name</label><input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="team-invite-first" /></div>
                <div><label className="text-xs font-medium text-slate-500">Last name</label><input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="team-invite-last" /></div>
              </div>
              <div><label className="text-xs font-medium text-slate-500">Email *</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="team-invite-email" /></div>
              <div><label className="text-xs font-medium text-slate-500">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="team-invite-role">
                  {(data?.assignable_roles || [{ value: "portfolio_admin", label: "Portfolio Administrator" }]).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button onClick={invite} disabled={busy === "invite"} data-testid="team-invite-submit"
                className="px-4 py-2 text-sm rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white inline-flex items-center gap-2">
                {busy === "invite" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
